import { useEffect, useRef, useState, useMemo } from "react";
import { Select } from "antd";
import type { configType } from "../types";
import {
  formatMetricValue,
  resolveThresholdColor,
  computeDelta,
} from "./computeMetric";

interface StatNumberProps {
  value: number;
  rows: Record<string, unknown>[];
  config: configType;
  isLoading: boolean;
}

const colorClasses = {
  default: "text-[#2B4752]",
  warn: "text-[#E5AB5F]",
  danger: "text-[#991962]",
} as const;

/** Smoothly counts up/down to the target value over ~600ms. */
function useCountUp(target: number, duration = 600): number {
  const [display, setDisplay] = useState(target);
  const startRef = useRef(target);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === display && startTimeRef.current === null) return;
    const from = display;
    startRef.current = from;
    startTimeRef.current = null;

    const animate = (now: number) => {
      if (startTimeRef.current === null) startTimeRef.current = now;
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (target - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setDisplay(target);
      }
    };

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return display;
}

export const StatNumber = ({
  value,
  rows,
  config,
  isLoading,
}: StatNumberProps) => {
  const mc = config.metricCard;
  const colorKey = mc ? resolveThresholdColor(value, mc) : "default";
  const valueColor = colorClasses[colorKey];
  const animated = useCountUp(isLoading ? 0 : value);

  const hasDelta = !!mc?.trendDateColumn;
  const periods = mc?.trendPeriods ?? [14, 30, 60, 90];
  const [selectedPeriod, setSelectedPeriod] = useState<number>(
    mc?.trendDefaultPeriod ?? periods[1] ?? periods[0],
  );

  const delta = useMemo(() => {
    if (!hasDelta || !mc) return null;
    return computeDelta(rows, mc, selectedPeriod);
  }, [hasDelta, rows, mc, selectedPeriod]);

  const periodOptions = periods.map((p) => ({
    label: p >= 365 ? "1y" : p >= 30 ? `${Math.round(p / 30)}mo` : `${p}d`,
    value: p,
  }));

  return (
    <div className="flex flex-col w-full p-4 gap-2">
      <p className="text-[13px] font-semibold text-[#556C75] leading-tight pr-7 truncate">
        {config.labels.title || "Metric"}
      </p>
      {config.labels.subtitle && (
        <p className="text-[11px] text-[#8C8C8C] leading-snug -mt-1 truncate">
          {config.labels.subtitle}
        </p>
      )}

      <div className="flex items-baseline gap-2 mt-1">
        {isLoading ? (
          <div className="h-9 w-20 bg-[#EFF1F2] animate-pulse rounded" />
        ) : (
          <>
            <p
              className={`text-[36px] font-semibold leading-none tabular-nums tracking-tight ${valueColor}`}
            >
              {formatMetricValue(animated)}
              {mc?.unit && (
                <span className="text-[18px] font-medium text-[#8C8C8C] ml-1">
                  {mc.unit}
                </span>
              )}
            </p>
            {delta && (
              <span
                className={`text-[12px] font-medium tabular-nums ${
                  delta.delta > 0
                    ? "text-[#5BB27F]"
                    : delta.delta < 0
                      ? "text-[#991962]"
                      : "text-[#8C8C8C]"
                }`}
              >
                {delta.delta > 0 ? "+" : ""}
                {formatMetricValue(delta.delta)}{" "}
                <span className="text-[#8C8C8C]">
                  ({delta.pct > 0 ? "+" : ""}
                  {delta.pct.toFixed(1)}%)
                </span>
              </span>
            )}
          </>
        )}
      </div>

      {hasDelta && (
        <div className="flex items-center gap-2 mt-0.5">
          <Select
            size="small"
            className="plotmodal"
            style={{ minWidth: 56 }}
            value={selectedPeriod}
            options={periodOptions}
            onChange={(v) => setSelectedPeriod(v)}
            popupMatchSelectWidth={false}
          />
          <span className="text-[11px] text-[#8C8C8C]">vs prior period</span>
        </div>
      )}
    </div>
  );
};
